import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { blogPosts } from '../data/blogs'

import SEO from '../components/SEO'

const BlogsPage: React.FC = () => {
    // Basic category filtering can still be useful, or we can just show all. 
    // User asked to remove "search option", likely referring to the search bar.
    // I'll keep it simple: Just show all posts or filtered by category if clicked.
    const [selectedCategory, setSelectedCategory] = useState<string>("All")

    const categories = ["All", ...Array.from(new Set(blogPosts.map(post => post.category)))]

    const displayedPosts = selectedCategory === "All"
        ? blogPosts
        : blogPosts.filter(post => post.category === selectedCategory)

    return (
        <div className="pt-24 pb-20 min-h-screen bg-gray-50">
            <SEO
                title="Real Estate Insights | Singh's Best Buy Blog"
                description="Expert analysis, investment tips, and luxury living guides for Gurgaon, Dehradun, and Haridwar real estate."
                keywords="Real Estate Blog, Property Investment Tips, Gurgaon Luxury Market, NRI Real Estate Guide"
            />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* Header */}
                <div className="text-center mb-16">
                    <h1 className="text-4xl md:text-5xl font-bold font-bricolage text-gray-900 mb-6">
                        Real Estate <span className="text-primary-500">Insights</span>
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
                        Stay ahead of the market with expert analysis, investment tips, and luxury living guides.
                    </p>

                    {/* Categories */}
                    <div className="flex flex-wrap justify-center gap-2 md:gap-4">
                        {categories.map(category => (
                            <button
                                key={category}
                                onClick={() => setSelectedCategory(category)}
                                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${selectedCategory === category
                                    ? 'bg-primary-500 text-white shadow-lg transform scale-105'
                                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                                    }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Blog Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {displayedPosts.map(post => (
                        <Link to={`/blog/${post.slug}`} key={post.id} className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col h-full border border-gray-100">
                            <div className="relative h-56 overflow-hidden">
                                <img
                                    src={post.image}
                                    alt={post.title}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                    loading="lazy"
                                />
                                <span className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider text-primary-600">
                                    {post.category}
                                </span>
                            </div>

                            <div className="p-6 flex flex-col flex-grow">
                                <div className="flex items-center text-sm text-gray-500 mb-4 space-x-4">
                                    <span className="flex items-center gap-1">🗓️ {post.date}</span>
                                    <span className="flex items-center gap-1">⏱️ {post.readTime}</span>
                                </div>

                                <h3 className="text-xl font-bold text-gray-900 mb-3 font-bricolage line-clamp-2 group-hover:text-primary-600 transition-colors">
                                    {post.title}
                                </h3>

                                <p className="text-gray-600 mb-6 line-clamp-3 text-sm flex-grow">
                                    {post.excerpt}
                                </p>

                                <span
                                    className="w-full py-3 bg-gray-50 text-gray-900 text-center rounded-xl font-semibold group-hover:bg-primary-500 group-hover:text-white transition-colors mt-auto block"
                                >
                                    Read Article
                                </span>
                            </div>
                        </Link>
                    ))}
                </div>

                {displayedPosts.length === 0 && (
                    <div className="text-center py-20 text-gray-500">
                        <p className="text-xl">No articles found in this category.</p>
                    </div>
                )}
            </div>
        </div>
    )
}

export default BlogsPage
